import { FC, ReactNode } from 'react';
import { isString } from 'fp-ts/string';
import { SidePanel } from '@mews/b2b-ui';
import { IconName, IconButton, Stack, Typography, Banner } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { SectionList, SectionType } from '@mews-commander/ui';
import { lt } from '@mews/framework';
import { AutomationIcon } from '../AutomationIcon';

export interface RoutingRuleDetailProps {
    title: ReactNode;
    description?: string;
    isDeleted?: boolean;
    footer?: ReactNode;
    loading?: boolean;
    sections: Array<SectionType>;
    opened: boolean;
    onClose: () => void;
    onEdit?: () => void;
}

export const RoutingRuleDetail: FC<RoutingRuleDetailProps> = ({
    title,
    onClose,
    opened,
    loading,
    onEdit,
    description,
    isDeleted,
    sections,
    footer,
}: RoutingRuleDetailProps) => (
    <SidePanel
        onClose={onClose}
        opened={opened}
        loading={loading}
        title={
            <>
                <span data-test-id={DataTestId.RoutingRulesDetailsDialogTitle}>
                    {title}
                    {isDeleted && (
                        <>
                            {UnicodeEntity.NoBreakSpace}
                            {UnicodeEntity.LeftParenthesis}
                            {lt(TextKey.Deleted)}
                            {UnicodeEntity.RightParenthesis}
                        </>
                    )}
                </span>
                <AutomationIcon />
            </>
        }
        width="medium"
        content={
            isDeleted ? (
                <Banner primaryText={lt('RoutingRuleDetailDeletedRoutingRuleBanner')} variant="warning" />
            ) : (
                <Stack vertical spacing="250">
                    {description && (
                        <Typography textStyle="bodyMedium" as="div">
                            {description}
                        </Typography>
                    )}
                    <SectionList sections={sections} />
                </Stack>
            )
        }
        headerActions={
            onEdit ? (
                <IconButton onClick={onEdit} variant="tertiary" size="small" icon={IconName.Edit} type="submit" />
            ) : null
        }
        actions={
            isString(footer) ? (
                <Typography variant="tertiary" textStyle="bodySmall" as="div">
                    {footer}
                </Typography>
            ) : (
                footer
            )
        }
    />
);
